import { TextNode } from 'lexical';

// Custom Lexical node that renders a misspelled word with red highlight.
// Extends TextNode so it participates in text selection and editing normally.
class MisspelledNode extends TextNode {
  static getType() {
    return 'misspelled';
  }

  static clone(node) {
    return new MisspelledNode(node.__text, node.__key);
  }

  // Copies custom properties to the clone (Lexical clones nodes on every state update)
  afterCloneFrom(prevNode) {
    super.afterCloneFrom(prevNode);
    this.__suggestions = prevNode.__suggestions;
    this.__reason = prevNode.__reason;
  }

  // Lexical convention: __ prefix marks node properties tracked by the state system
  constructor(text, key) {
    super(text, key);
    this.__suggestions = [];
    this.__reason = '';
  }

  // Lexical calls this to build the actual DOM element for this node
  createDOM(config) {
    const dom = super.createDOM(config);
    dom.style.backgroundColor = '#FBB2B2';
    dom.style.textDecoration = 'underline #D60D0D';
    dom.style.textDecorationThickness = '2px';
    dom.style.cursor = 'pointer';
    dom.setAttribute('data-misspelled', 'true');
    dom.setAttribute('data-suggestions', JSON.stringify(this.__suggestions || []));
    dom.setAttribute('data-reason', this.__reason || '');
    return dom;
  }

  // Called on state changes; returns true if Lexical should recreate the DOM element entirely
  updateDOM(prevNode, dom, config) {
    if (super.updateDOM(prevNode, dom, config)) {
      return true;
    }
    dom.setAttribute('data-suggestions', JSON.stringify(this.__suggestions || []));
    dom.setAttribute('data-reason', this.__reason || '');
    return false;
  }

  setSuggestions(suggestions) {
    // Lexical nodes are frozen; getWritable() returns a mutable clone for this update cycle
    const writable = this.getWritable();
    writable.__suggestions = suggestions;
    return writable;
  }

  setReason(reason) {
    const writable = this.getWritable();
    writable.__reason = reason;
    return writable;
  }

  getSuggestions() {
    return this.getLatest().__suggestions || [];
  }

  getReason() {
    return this.getLatest().__reason || 'Misspelled word';
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'misspelled',
      suggestions: this.__suggestions,
      reason: this.__reason,
    };
  }

  static importJSON(serializedNode) {
    return new MisspelledNode(serializedNode.text).updateFromJSON(serializedNode);
  }

  updateFromJSON(serializedNode) {
    return super.updateFromJSON(serializedNode).setSuggestions(serializedNode.suggestions).setReason(serializedNode.reason);
  }
}

export default MisspelledNode;
